import React, { useState, useEffect } from 'react';
import { 
  Mail, Server, Key, User, Shield, CheckCircle, AlertCircle, 
  Send, RefreshCw, Eye, EyeOff, Save, ExternalLink 
} from 'lucide-react';

export default function EmailSettings({ onNavigateTab }) {
  const [formData, setFormData] = useState({
    host: '',
    port: 25,
    auth_type: 'tls',
    username: '',
    password: ''
  });

  const [testEmail, setTestEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // { type: 'success' | 'error' | 'info', message: '' }

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // { type: 'success' | 'error', message: '' }
  
  const [teams, setTeams] = useState([]);

  useEffect(() => {
    fetchSettings();
    fetchTeams();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/settings/smtp');
      const data = await res.json();
      if (data && data.host) {
        setFormData({
          host: data.host || '',
          port: data.port || 25,
          auth_type: data.auth_type || 'tls',
          username: data.username || '',
          password: data.password || ''
        });
      }
    } catch (err) {
      console.error('Error fetching SMTP settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeams = async () => {
    try {
      const res = await fetch('/api/teams');
      const data = await res.json();
      if (data.status === 'success') {
        setTeams(data.data || []);
      }
    } catch (err) {
      console.error('Error fetching teams:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'port' ? parseInt(value, 10) || '' : value
    }));
    setSaveStatus(null);
  };

  const handleApplyPreset = () => {
    setFormData({
      host: 'tech.tmh-wst.com',
      port: 25,
      auth_type: 'tls',
      username: 'web-smtp@tech.tmh-wst.com',
      password: 'Toyota@2025'
    });
    setSaveStatus({
      type: 'info',
      message: 'กรอกข้อมูลเริ่มต้น TMHWST เรียบร้อยแล้ว โปรดกดปุ่มบันทึกการตั้งค่า'
    });
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    setSaving(true);
    setSaveStatus(null);

    try {
      const res = await fetch('/api/settings/smtp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          port: parseInt(formData.port, 10) || 25
        })
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setSaveStatus({
          type: 'success',
          message: 'บันทึกการตั้งค่า SMTP เรียบร้อยแล้ว พร้อมใช้งานในระบบทันที'
        });
      } else {
        setSaveStatus({
          type: 'error',
          message: 'เกิดข้อผิดพลาดในการบันทึก: ' + (data.error || data.message || 'Unknown error')
        });
      }
    } catch (err) {
      setSaveStatus({
        type: 'error',
        message: 'ไม่สามารถติดต่อเซิร์ฟเวอร์ได้: ' + err.message
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async (e) => {
    e.preventDefault();
    if (!testEmail || !testEmail.trim()) {
      setTestResult({
        type: 'error',
        message: 'กรุณาระบุที่อยู่อีเมลปลายทางสำหรับรับอีเมลทดสอบ'
      });
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const res = await fetch('/api/settings/smtp/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          host: formData.host,
          port: parseInt(formData.port, 10) || 25,
          auth_type: formData.auth_type,
          username: formData.username,
          password: formData.password,
          to_email: testEmail.trim()
        })
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setTestResult({
          type: 'success',
          message: data.message || `ส่งอีเมลทดสอบไปยัง ${testEmail} สำเร็จเรียบร้อย!`
        });
      } else {
        setTestResult({
          type: 'error',
          message: data.message || 'ส่งอีเมลทดสอบล้มเหลว'
        });
      }
    } catch (err) {
      setTestResult({
        type: 'error',
        message: 'เกิดข้อผิดพลาดในการส่ง: ' + err.message
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem', color: '#64748b' }}>
        <RefreshCw size={24} className="animate-spin" style={{ marginRight: '0.75rem' }} />
        กำลังโหลดการตั้งค่า SMTP...
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', maxWidth: '1000px', margin: '0 auto' }}>
      
      {/* Header Banner */}
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: '1.5rem 2rem',
        border: '1px solid #e2e8f0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '10px',
            backgroundColor: '#fee2e2',
            color: '#eb0a1e',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Mail size={24} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#0f172a', fontWeight: 700 }}>
              ตั้งค่าระบบส่งอีเมลแจ้งเตือน (SMTP Settings)
            </h3>
            <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: '#64748b' }}>
              กำหนดค่า SMTP Server สำหรับส่งอีเมลแจ้งเตือน Lead ใหม่ไปยังทีมฝ่ายขายอัตโนมัติ
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleApplyPreset}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            borderRadius: '8px',
            border: '1px solid #cbd5e1',
            backgroundColor: '#f8fafc',
            color: '#334155',
            fontSize: '0.85rem',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          title="ใส่ค่าเริ่มต้นของ TMHWST อัตโนมัติ"
        >
          <Server size={16} />
          ใช้ค่าเริ่มต้น TMHWST
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(460px, 1fr))', gap: '1.5rem' }}>
        
        {/* Card 1: SMTP Config Form */}
        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          padding: '1.75rem',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <h4 style={{ margin: '0 0 1.25rem 0', fontSize: '1rem', fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Server size={18} color="#eb0a1e" />
            ข้อมูลการเชื่อมต่อ SMTP Server
          </h4>

          {saveStatus && (
            <div style={{
              padding: '0.75rem 1rem',
              borderRadius: '8px',
              marginBottom: '1.25rem',
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.5rem',
              backgroundColor: saveStatus.type === 'success' ? '#f0fdf4' : (saveStatus.type === 'info' ? '#eff6ff' : '#fef2f2'),
              color: saveStatus.type === 'success' ? '#166534' : (saveStatus.type === 'info' ? '#1d4ed8' : '#991b1b'),
              border: `1px solid ${saveStatus.type === 'success' ? '#bbf7d0' : (saveStatus.type === 'info' ? '#bfdbfe' : '#fecaca')}`
            }}>
              {saveStatus.type === 'success' ? <CheckCircle size={18} style={{ flexShrink: 0, marginTop: '2px' }} /> : <AlertCircle size={18} style={{ flexShrink: 0, marginTop: '2px' }} />}
              <div>{saveStatus.message}</div>
            </div>
          )}

          <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '1rem', flex: 1 }}>
            
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.75rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '0.35rem' }}>
                  SMTP Host <span style={{ color: '#eb0a1e' }}>*</span>
                </label>
                <input
                  type="text"
                  name="host"
                  value={formData.host}
                  onChange={handleChange}
                  required
                  placeholder="tech.tmh-wst.com"
                  style={{
                    width: '100%',
                    padding: '0.65rem 0.85rem',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '0.9rem',
                    outline: 'none'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '0.35rem' }}>
                  Port <span style={{ color: '#eb0a1e' }}>*</span>
                </label>
                <input
                  type="number"
                  name="port"
                  value={formData.port}
                  onChange={handleChange}
                  required
                  placeholder="25, 465, 587"
                  style={{
                    width: '100%',
                    padding: '0.65rem 0.85rem',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '0.9rem',
                    outline: 'none'
                  }}
                />
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '0.35rem' }}>
                Authentication Type
              </label>
              <select
                name="auth_type"
                value={formData.auth_type}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '0.65rem 0.85rem',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  fontSize: '0.9rem',
                  outline: 'none',
                  backgroundColor: '#ffffff'
                }}
              >
                <option value="tls">TLS / STARTTLS (Port 25 / 587)</option>
                <option value="ssl">SSL Direct (Port 465)</option>
                <option value="none">None (ไม่มีการยืนยันตัวตน)</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '0.35rem' }}>
                Sender Username / Email <span style={{ color: '#eb0a1e' }}>*</span>
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  placeholder="web-smtp@tech.tmh-wst.com"
                  style={{
                    width: '100%',
                    padding: '0.65rem 0.85rem 0.65rem 2.25rem',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '0.9rem',
                    outline: 'none'
                  }}
                />
                <User size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '0.35rem' }}>
                Password <span style={{ color: '#eb0a1e' }}>*</span>
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder="SMTP Password"
                  style={{
                    width: '100%',
                    padding: '0.65rem 2.5rem 0.65rem 2.25rem',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '0.9rem',
                    outline: 'none'
                  }}
                />
                <Key size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: '#94a3b8',
                    cursor: 'pointer',
                    padding: 0
                  }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div style={{
              marginTop: '0.5rem',
              padding: '0.75rem',
              borderRadius: '6px',
              backgroundColor: '#f8fafc',
              border: '1px dashed #cbd5e1',
              fontSize: '0.75rem',
              color: '#64748b',
              lineHeight: 1.5
            }}>
              💡 <b>คำแนะนำ:</b> เซิร์ฟเวอร์ <code>tech.tmh-wst.com:25</code> รองรับ Plain AUTH PLAIN/LOGIN ในโหมด Port 25 โดยระบบรองรับการเข้ารหัสหัวข้อจดหมายภาษาไทย (MIME UTF-8 Base64) โดยสมบูรณ์
            </div>

            <div style={{ marginTop: 'auto', paddingTop: '1rem' }}>
              <button
                type="submit"
                disabled={saving}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: '#eb0a1e',
                  color: '#ffffff',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.7 : 1,
                  boxShadow: '0 2px 4px rgba(235, 10, 30, 0.2)'
                }}
              >
                <Save size={18} />
                {saving ? 'กำลังบันทึก...' : 'บันทึกการตั้งค่า (Save Settings)'}
              </button>
            </div>

          </form>
        </div>

        {/* Card 2: Test Email & Team Overview */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          
          {/* Test Email Section */}
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '1.75rem',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Send size={18} color="#2563eb" />
              ทดสอบส่งอีเมล (Test SMTP Connection)
            </h4>
            <p style={{ margin: '0 0 1.25rem 0', fontSize: '0.825rem', color: '#64748b' }}>
              ทดสอบเชื่อมต่อและส่งอีเมลจริงด้วยการตั้งค่าข้างต้น เพื่อยืนยันว่าไม่มีข้อผิดพลาด
            </p>

            {testResult && (
              <div style={{
                padding: '0.85rem 1rem',
                borderRadius: '8px',
                marginBottom: '1rem',
                fontSize: '0.85rem',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5rem',
                backgroundColor: testResult.type === 'success' ? '#f0fdf4' : '#fef2f2',
                color: testResult.type === 'success' ? '#166534' : '#991b1b',
                border: `1px solid ${testResult.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
                wordBreak: 'break-word'
              }}>
                {testResult.type === 'success' ? <CheckCircle size={18} style={{ flexShrink: 0, marginTop: '2px' }} /> : <AlertCircle size={18} style={{ flexShrink: 0, marginTop: '2px' }} />}
                <div>{testResult.message}</div>
              </div>
            )}

            <form onSubmit={handleSendTest} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '0.35rem' }}>
                  ส่งอีเมลทดสอบไปยัง (Recipient Email) <span style={{ color: '#eb0a1e' }}>*</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="email"
                    value={testEmail}
                    onChange={(e) => {
                      setTestEmail(e.target.value);
                      setTestResult(null);
                    }}
                    required
                    placeholder="เช่น techin.naja@gmail.com"
                    style={{
                      width: '100%',
                      padding: '0.65rem 0.85rem 0.65rem 2.25rem',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      fontSize: '0.9rem',
                      outline: 'none'
                    }}
                  />
                  <Mail size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                </div>
              </div>

              <button
                type="submit"
                disabled={testing}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1.25rem',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: '#2563eb',
                  color: '#ffffff',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  cursor: testing ? 'not-allowed' : 'pointer',
                  opacity: testing ? 0.7 : 1,
                  boxShadow: '0 2px 4px rgba(37, 99, 235, 0.2)'
                }}
              >
                {testing ? <RefreshCw size={16} className="animate-spin" /> : <Send size={16} />}
                {testing ? 'กำลังเชื่อมต่อและส่งอีเมล...' : 'ส่งอีเมลทดสอบ (Send Test Email)'}
              </button>
            </form>
          </div>

          {/* Quick Team Email Overview Card */}
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '1.5rem',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Shield size={16} color="#059669" />
                รายชื่ออีเมลผู้รับประจำแต่ละทีม ({teams.length} ทีม)
              </h4>
              {onNavigateTab && (
                <button
                  type="button"
                  onClick={() => onNavigateTab('teamemails')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#eb0a1e',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    padding: 0
                  }}
                >
                  แก้ไขอีเมลทีม <ExternalLink size={12} />
                </button>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '200px', overflowY: 'auto' }}>
              {teams.map(t => (
                <div key={t.id} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.5rem 0.75rem',
                  borderRadius: '6px',
                  backgroundColor: '#f8fafc',
                  border: '1px solid #f1f5f9',
                  fontSize: '0.825rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, color: '#334155' }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: t.team_color || '#eb0a1e' }}></span>
                    {t.team_name}
                  </div>
                  <div style={{ color: t.team_email ? '#0f172a' : '#94a3b8', fontStyle: t.team_email ? 'normal' : 'italic' }}>
                    {t.team_email || 'ยังไม่ได้ตั้งค่าอีเมล'}
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

      </div>

    </div>
  );
}
